/**
 * useOfferActions Hook
 *
 * Shared hook for offer-related actions (accept, cancel) with Xaman integration.
 * Used by WalletPage, NFTDetailPage, and OffersPage.
 */

import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { apiClient } from '../api/client';
import { toSigningPayload } from '../lib/wallet';
import type { SigningPayload } from '../types/signing';
import { isSupportedMarketplace, calculateSellAmountForBuyOffer } from '../utils/constants';

export type OfferOperation = 'cancel' | 'acceptTransfer' | 'rejectTransfer' | 'acceptBuyOffer' | 'acceptSellOffer';

interface UseOfferActionsOptions {
  /** Query keys to invalidate on success */
  invalidateKeys?: string[][];
  /** Callback to optimistically remove an offer from local state (called before cache invalidation) */
  onOfferRemoved?: (offerId: string, operation: OfferOperation) => void;
}

interface OfferActionState {
  processing: boolean;
  showXamanModal: boolean;
  xamanPayload: SigningPayload | null;
  currentOperation: OfferOperation | null;
  currentOfferId: string | null;
}

export function useOfferActions(options: UseOfferActionsOptions = {}) {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  const [state, setState] = useState<OfferActionState>({
    processing: false,
    showXamanModal: false,
    xamanPayload: null,
    currentOperation: null,
    currentOfferId: null,
  });

  /**
   * Cancel an offer (sell offer, buy offer, or transfer)
   */
  const cancelOffer = async (offerId: string, nftTokenId: string) => {
    if (!isAuthenticated || !user?.xrpl_address) {
      showError('You must be logged in to cancel an offer');
      return;
    }

    setState(s => ({ ...s, processing: true, currentOfferId: offerId }));

    try {
      const response = await apiClient.post<any>('/offers/cancel', {
        nftTokenId,
        offerIndex: offerId,
        userAddress: user.xrpl_address
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setState(s => ({
          ...s,
          xamanPayload: toSigningPayload(response.data.signingIntent ?? response.data.payload),
          currentOperation: 'cancel',
          showXamanModal: true
        }));
      } else {
        showError(response.error || 'Failed to cancel offer');
        setState(s => ({ ...s, processing: false }));
      }
    } catch (err) {
      showError('Failed to cancel offer');
      setState(s => ({ ...s, processing: false }));
    }
  };

  /**
   * Accept an incoming transfer (zero-value sell offer destined to user)
   */
  const acceptTransfer = async (offerId: string, nftTokenId: string) => {
    if (!isAuthenticated || !user?.xrpl_address) {
      showError('You must be logged in to accept a transfer');
      return;
    }

    setState(s => ({ ...s, processing: true, currentOfferId: offerId }));

    try {
      const response = await apiClient.post<any>('/offers/accept-p2p', {
        nftTokenId,
        offerIndex: offerId,
        userAddress: user.xrpl_address,
        offerType: 'sell'
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setState(s => ({
          ...s,
          xamanPayload: toSigningPayload(response.data.signingIntent ?? response.data.payload),
          currentOperation: 'acceptTransfer',
          showXamanModal: true
        }));
      } else {
        showError(response.error || 'Failed to accept transfer');
        setState(s => ({ ...s, processing: false }));
      }
    } catch (err) {
      showError('Failed to accept transfer');
      setState(s => ({ ...s, processing: false }));
    }
  };

  /**
   * Reject an incoming transfer (cancel the offer as the destination)
   */
  const rejectTransfer = async (offerId: string, nftTokenId: string) => {
    if (!isAuthenticated || !user?.xrpl_address) {
      showError('You must be logged in to reject a transfer');
      return;
    }

    setState(s => ({ ...s, processing: true, currentOfferId: offerId }));

    try {
      const response = await apiClient.post<any>('/offers/cancel', {
        nftTokenId,
        offerIndex: offerId,
        userAddress: user.xrpl_address
      });

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setState(s => ({
          ...s,
          xamanPayload: toSigningPayload(response.data.signingIntent ?? response.data.payload),
          currentOperation: 'rejectTransfer',
          showXamanModal: true
        }));
      } else {
        showError(response.error || 'Failed to reject transfer');
        setState(s => ({ ...s, processing: false }));
      }
    } catch (err) {
      showError('Failed to reject transfer');
      setState(s => ({ ...s, processing: false }));
    }
  };

  /**
   * Accept a buy offer (sell NFT to buyer)
   *
   * For marketplace-brokered buy offers (destination != null):
   *   Creates a matching sell offer via /offers/create with the same destination.
   *   The seller pays the fee: sellAmount = buyAmount * (1 - feePercentage).
   *
   * For P2P buy offers (destination == null):
   *   Directly accepts the buy offer via /offers/accept-p2p.
   *
   * @param offerId - The buy offer's XRPL offer index
   * @param nftTokenId - The NFT token ID
   * @param buyOfferDestination - The buy offer's destination (marketplace broker address or null for P2P)
   * @param buyOfferAmount - The buy offer amount in XRP drops
   */
  const acceptBuyOffer = async (
    offerId: string,
    nftTokenId: string,
    buyOfferDestination: string | null = null,
    buyOfferAmount: number = 0
  ) => {
    if (!isAuthenticated || !user?.xrpl_address) {
      showError('You must be logged in to accept an offer');
      return;
    }

    setState(s => ({ ...s, processing: true, currentOfferId: offerId }));

    try {
      // Check if this is a marketplace-brokered buy offer
      const isMarketplaceBrokered = buyOfferDestination && isSupportedMarketplace(buyOfferDestination);

      // Reject unsupported marketplaces (has destination but not in our supported list)
      if (buyOfferDestination && !isSupportedMarketplace(buyOfferDestination)) {
        showError('This offer uses an unsupported marketplace. Cannot accept.');
        setState(s => ({ ...s, processing: false }));
        return;
      }

      let response;

      if (isMarketplaceBrokered) {
        // For marketplace-brokered buy offers, we need to CREATE a sell offer with:
        // - The same destination as the buy offer (marketplace broker)
        // - Seller pays the fee: Amount = buyAmount * (1 - fee%)

        // Validate amount is provided for marketplace-brokered offers
        if (buyOfferAmount <= 0) {
          showError('Buy offer amount is required to accept a marketplace-brokered offer.');
          setState(s => ({ ...s, processing: false }));
          return;
        }

        const sellAmount = calculateSellAmountForBuyOffer(buyOfferAmount, buyOfferDestination);

        response = await apiClient.post<any>('/offers/create', {
          nftTokenId,
          offerType: 'sell',
          amount: sellAmount,
          userAddress: user.xrpl_address,
          destination: buyOfferDestination
        });
      } else {
        // For P2P buy offers, directly accept the buy offer
        response = await apiClient.post<any>('/offers/accept-p2p', {
          nftTokenId,
          offerIndex: offerId,
          userAddress: user.xrpl_address,
          offerType: 'buy'
        });
      }

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setState(s => ({
          ...s,
          xamanPayload: toSigningPayload(response.data.signingIntent ?? response.data.payload),
          currentOperation: 'acceptBuyOffer',
          showXamanModal: true
        }));
      } else {
        showError(response.error || 'Failed to accept offer');
        setState(s => ({ ...s, processing: false }));
      }
    } catch (err) {
      showError('Failed to accept offer');
      setState(s => ({ ...s, processing: false }));
    }
  };

  /**
   * Accept a sell offer (buy an NFT)
   *
   * For marketplace-brokered sell offers (destination != null):
   *   Uses /offers/accept to create a buy offer with marketplace fee.
   *
   * For P2P sell offers (destination == null):
   *   Directly accepts the sell offer via /offers/accept-p2p.
   *
   * @param offerId - The sell offer's XRPL offer index
   * @param nftTokenId - The NFT token ID
   * @param sellOfferDestination - The sell offer's destination (marketplace broker address or null for P2P)
   */
  const acceptSellOffer = async (
    offerId: string,
    nftTokenId: string,
    sellOfferDestination: string | null = null
  ) => {
    if (!isAuthenticated || !user?.xrpl_address) {
      showError('You must be logged in to accept an offer');
      return;
    }

    setState(s => ({ ...s, processing: true, currentOfferId: offerId }));

    try {
      // Reject unsupported marketplaces (has destination but not in our supported list)
      if (sellOfferDestination && !isSupportedMarketplace(sellOfferDestination)) {
        showError('This offer uses an unsupported marketplace. Cannot accept.');
        setState(s => ({ ...s, processing: false }));
        return;
      }

      // Check if this is a marketplace-brokered sell offer
      const isMarketplaceBrokered = sellOfferDestination && isSupportedMarketplace(sellOfferDestination);

      // Use appropriate endpoint based on whether it's marketplace-brokered
      const endpoint = isMarketplaceBrokered ? '/offers/accept' : '/offers/accept-p2p';
      const body = isMarketplaceBrokered
        ? { nftTokenId, offerIndex: offerId, userAddress: user.xrpl_address }
        : { nftTokenId, offerIndex: offerId, userAddress: user.xrpl_address, offerType: 'sell' };

      const response = await apiClient.post<any>(endpoint, body);

      if (response.success && (response.data?.signingIntent || response.data?.payload)) {
        setState(s => ({
          ...s,
          xamanPayload: toSigningPayload(response.data.signingIntent ?? response.data.payload),
          currentOperation: 'acceptSellOffer',
          showXamanModal: true
        }));
      } else {
        showError(response.error || 'Failed to accept offer');
        setState(s => ({ ...s, processing: false }));
      }
    } catch (err) {
      showError('Failed to accept offer');
      setState(s => ({ ...s, processing: false }));
    }
  };

  /**
   * Handle successful transaction signing
   */
  const handleTransactionSuccess = (hash?: string) => {
    const { currentOperation, currentOfferId } = state;
    const userAddress = user?.xrpl_address;

    // Optimistically remove the offer from local state
    // This provides immediate UI feedback while the XRPL transaction propagates
    if (currentOfferId && currentOperation && options.onOfferRemoved) {
      options.onOfferRemoved(currentOfferId, currentOperation);
    }

    // Immediately delete the transfer offer from the DB so it doesn't
    // reappear on refresh (don't wait for ingestion to process the on-chain tx).
    // Also pass the action so the notification can be stamped with the outcome.
    const isTransferOrCancel = currentOperation === 'acceptTransfer' || currentOperation === 'rejectTransfer' || currentOperation === 'cancel';
    if (currentOfferId && isTransferOrCancel) {
      const action = currentOperation === 'acceptTransfer' ? 'accepted'
        : currentOperation === 'rejectTransfer' ? 'rejected'
        : undefined;
      apiClient.post('/offers/transfer/confirm', { offerId: currentOfferId, ...(action && { action }) }).catch(() => {});
    }

    setState({
      processing: false,
      showXamanModal: false,
      xamanPayload: null,
      currentOperation: null,
      currentOfferId: null,
    });

    if (options.invalidateKeys && userAddress) {
      // For transfer/cancel operations, the DB delete is immediate so we can
      // bust the Redis cache right away — no need to wait for XRPL propagation.
      // For buy/sell operations, delay 10s to let the on-chain state settle
      // before refetching from XRPL data APIs.
      const invalidate = async () => {
        try {
          await apiClient.post(`/wallets/${userAddress}/invalidate-cache`, {});
        } catch (err) {
          console.warn('Failed to invalidate backend cache:', err);
        }
        options.invalidateKeys!.forEach(key => {
          queryClient.invalidateQueries({ queryKey: key });
        });
      };

      if (isTransferOrCancel) {
        invalidate();
      } else {
        setTimeout(invalidate, 10000);
      }
    }

    if (hash) {
      const messages: Record<OfferOperation, string> = {
        cancel: 'Offer cancelled successfully!',
        acceptTransfer: 'Transfer accepted! NFT received.',
        rejectTransfer: 'Transfer rejected.',
        acceptBuyOffer: 'Offer accepted! NFT sold.',
        acceptSellOffer: 'Purchase complete! NFT received.',
      };
      showSuccess(currentOperation ? messages[currentOperation] : 'Transaction successful!');
    }
  };

  /**
   * Handle rejected or expired transaction
   */
  const handleTransactionRejected = () => {
    setState({
      processing: false,
      showXamanModal: false,
      xamanPayload: null,
      currentOperation: null,
      currentOfferId: null,
    });
  };

  /**
   * Get modal title based on current operation
   */
  const getModalTitle = (): string => {
    switch (state.currentOperation) {
      case 'cancel': return 'Cancel Offer';
      case 'acceptTransfer': return 'Accept Transfer';
      case 'rejectTransfer': return 'Reject Transfer';
      case 'acceptBuyOffer': return 'Accept Offer';
      case 'acceptSellOffer': return 'Complete Purchase';
      default: return 'Sign Transaction';
    }
  };

  return {
    // State
    processing: state.processing,
    showXamanModal: state.showXamanModal,
    xamanPayload: state.xamanPayload,
    currentOfferId: state.currentOfferId,

    // Actions
    cancelOffer,
    acceptTransfer,
    rejectTransfer,
    acceptBuyOffer,
    acceptSellOffer,

    // Modal handlers
    handleTransactionSuccess,
    handleTransactionRejected,
    getModalTitle,
  };
}
